import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { ANY_RELOAD_TARGET, parseReloadAttempt, parseVersionFile, RELOAD_ATTEMPT_KEY, ReloadAttempt, VERSION_FILE } from './app-update-rules';

/**
 * A frissítés-figyelő böngészős mellékhatásai egy helyen, hogy a tesztek kicserélhessék.
 * Spec: docs/superpowers/specs/2026-09-16-app-update-detection-design.md (7.4)
 */
@Injectable({
  providedIn: 'root'
})
export class AppUpdateBrowser {
  private readonly document = inject(DOCUMENT);

  /** Natív fetch (nem HttpClient), így az interceptorok nem nyúlnak hozzá. Hibát nem dob. */
  async fetchRemoteBuildId(): Promise<string | null> {
    try {
      const response = await fetch(new URL(VERSION_FILE, this.document.baseURI), { cache: 'no-store' });
      return response.ok ? parseVersionFile(await response.json()) : null;
    } catch {
      return null;
    }
  }

  reload(): void {
    this.document.location.reload();
  }

  assign(url: string): void {
    this.document.location.assign(url);
  }

  replace(url: string): void {
    this.document.location.replace(url);
  }

  /** Elérhetetlen sessionStorage esetén „minden célra” szóló friss kísérlet: nincs automatikus újratöltés. */
  readAttempt(): ReloadAttempt | null {
    try {
      return parseReloadAttempt(this.sessionStorage().getItem(RELOAD_ATTEMPT_KEY));
    } catch {
      return { target: ANY_RELOAD_TARGET, at: this.now() };
    }
  }

  writeAttempt(attempt: ReloadAttempt): void {
    this.ignoreStorageErrors(() => this.sessionStorage().setItem(RELOAD_ATTEMPT_KEY, JSON.stringify(attempt)));
  }

  clearAttempt(): void {
    this.ignoreStorageErrors(() => this.sessionStorage().removeItem(RELOAD_ATTEMPT_KEY));
  }

  isVisible(): boolean {
    return this.document.visibilityState === 'visible';
  }

  onVisible(callback: () => void): () => void {
    const listener = (): void => {
      if (this.isVisible()) {
        callback();
      }
    };
    this.document.addEventListener('visibilitychange', listener);
    return () => this.document.removeEventListener('visibilitychange', listener);
  }

  every(intervalMs: number, callback: () => void): () => void {
    const handle = setInterval(callback, intervalMs);
    return () => clearInterval(handle);
  }

  now(): number {
    return Date.now();
  }

  private sessionStorage(): Storage {
    const storage = this.document.defaultView?.sessionStorage;
    if (!storage) {
      throw new Error('A sessionStorage nem elérhető');
    }
    return storage;
  }

  private ignoreStorageErrors(action: () => void): void {
    try {
      action();
    } catch {
      // Tiltott sessionStorage: ilyenkor a readAttempt tiltja az automatikus újratöltést.
    }
  }
}
